import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { ActionIcon, Button, Group, NumberInput, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';

export interface ListField<T> {
  key: keyof T & string;
  label: string;
  type?: 'text' | 'longtext' | 'date' | 'number' | 'select';
  options?: { value: string; label: string }[];
  /** Flex grow; fixed width when `width` is given. */
  width?: number;
  grow?: number;
  placeholder?: string;
  required?: boolean;
}

/**
 * A small editable table for the lists of the project data - assumptions,
 * risks, the week grid. Rows are edited in place; the delete cross sits on the
 * left, as everywhere in PST; changes are saved together.
 */
export function ListEditor<T extends Record<string, unknown>>({
  items,
  fields,
  empty,
  onSave,
  saving,
  readOnly,
  addLabel = 'Zeile hinzufügen',
}: {
  items: T[];
  fields: ListField<T>[];
  empty: () => T;
  onSave: (items: T[]) => void;
  saving?: boolean;
  readOnly?: boolean;
  addLabel?: string;
}): JSX.Element {
  const [rows, setRows] = useState<T[]>(items);
  useEffect(() => setRows(items), [items]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(items);

  const set = (index: number, key: keyof T, value: unknown): void =>
    setRows((previous) => previous.map((row, i) => (i === index ? { ...row, [key]: value } : row)));

  if (readOnly && rows.length === 0) return <Text size="sm" c="dimmed">Keine Einträge.</Text>;

  return (
    <Stack gap={6}>
      {rows.map((row, index) => (
        <Group key={index} gap={6} wrap="nowrap" align="flex-start">
          {!readOnly && (
            <ActionIcon
              variant="subtle"
              color="gray"
              mt={4}
              aria-label="Zeile entfernen"
              title="Zeile entfernen"
              onClick={() => setRows((previous) => previous.filter((_, i) => i !== index))}
            >
              {'✕'}
            </ActionIcon>
          )}
          {fields.map((field) => {
            const style = field.width ? { width: field.width, flex: '0 0 auto' } : { flex: field.grow ?? 1, minWidth: 0 };
            const common = {
              'aria-label': field.label,
              placeholder: index === 0 ? field.placeholder ?? field.label : field.placeholder,
              readOnly,
              size: 'sm' as const,
              style,
            };
            const value = row[field.key];
            if (field.type === 'select') {
              return (
                <Select
                  key={field.key}
                  {...common}
                  placeholder={field.placeholder}
                  data={field.options ?? []}
                  value={(value as string | null) ?? null}
                  onChange={(next) => set(index, field.key, next)}
                  allowDeselect={false}
                />
              );
            }
            if (field.type === 'number') {
              return (
                <NumberInput
                  key={field.key}
                  {...common}
                  value={(value as number | null) ?? ''}
                  onChange={(next) => set(index, field.key, next === '' ? null : Number(next))}
                  hideControls
                />
              );
            }
            if (field.type === 'longtext') {
              return (
                <Textarea
                  key={field.key}
                  {...common}
                  autosize
                  minRows={1}
                  maxRows={6}
                  value={(value as string | null) ?? ''}
                  onChange={(event) => set(index, field.key, event.currentTarget.value)}
                />
              );
            }
            return (
              <TextInput
                key={field.key}
                {...common}
                type={field.type === 'date' ? 'date' : 'text'}
                value={(value as string | null) ?? ''}
                onChange={(event) => set(index, field.key, event.currentTarget.value)}
              />
            );
          })}
        </Group>
      ))}
      {rows.length === 0 && <Text size="sm" c="dimmed">Noch keine Einträge.</Text>}
      {!readOnly && (
        <Group justify="space-between">
          <Button variant="subtle" size="xs" onClick={() => setRows((previous) => [...previous, empty()])}>
            + {addLabel}
          </Button>
          {dirty && (
            <Group gap="xs">
              <Button variant="default" size="xs" onClick={() => setRows(items)}>Verwerfen</Button>
              <Button
                size="xs"
                loading={saving}
                onClick={() =>
                  onSave(rows.filter((row) => fields.filter((f) => f.required).every((f) => String(row[f.key] ?? '').trim() !== '')))
                }
              >
                Speichern
              </Button>
            </Group>
          )}
        </Group>
      )}
    </Stack>
  );
}
